import React, { useState, useEffect, useMemo, memo } from "react";
import "../../App.css";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { customStyles } from "./../../helpers/StyleUtils";
import { bookingService, userService, locationService, companyService } from "../../api/apiService";
import { constcenter1, constcenter2 } from "../../helpers/costCenters";

const MemoizedSelect = memo(Select);

function BookingDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [status, setStatus] = useState("");
  const [propsoedBy, setpropsoedBy] = useState("");
  const [kpi, setKpi] = useState("");
  const bookingId = new URLSearchParams(location.search).get("id");
  const [superAdminOptions, setSuperAdminOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedSet, setSelectedSet] = useState(null);


  const setOptions = useMemo(() => [
    { value: "On Model", label: "On Model" },
    { value: "On Produkt", label: "On Produkt" },
  ], []);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [date, setDate] = useState("");
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [selectedCostCenterTwo, setSelectedCostCenterTwo] = useState(null);

  const bookingStatusOptions = useMemo(() => [
    { value: "Booked", label: "booked / Lano task created" },
    { value: "confirmed", label: "confirmed" },
    { value: "new order", label: "new order" },
    { value: "requested", label: "requested" },
    { value: "declined request", label: "declined request" },
    { value: "declined", label: "declined" },
    { value: "canceled", label: "canceled" },
    { value: "optioned", label: "optioned" },
    { value: "optionSolved", label: "option released" },
    { value: "talentOnHold", label: "Talent On Hold" },
  ], []);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000);
      if (response.data && response.data.locations) {
        const formattedLocations = response.data.locations
          .filter(location => location.short !== 'OSB')
          .map(location => ({
            value: location.id,
            label: location.short || location.name, // Prioritize short name over full name
            locationData: location
          })).sort((a, b) => a.label.localeCompare(b.label));
        setLocationOptions(formattedLocations);
        return formattedLocations;
      } else {
        console.error("No location data found in API response");
        setLocationOptions([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocationOptions([]);
      return [];
    }
  };

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setNotFound(true);
        // console.log("No booking ID found");
        setLoading(false);
        return;
      }
      
      try {
        // console.log("Fetching booking details for ID:", bookingId);
        const response = await bookingService.getBookingById(bookingId);

        if (!response.data) {
          // console.log("Booking not found");
          setNotFound(true);
          setLoading(false);
          return;
        }

        const bookingData = response.data;
        setBookingDetails(bookingData);
        setStatus(bookingStatusOptions.find(option => option.value === bookingData.status));
        setStartTime(bookingData.start_time || bookingData.startTime || "");
        setEndTime(bookingData.end_time || bookingData.endTime || "");

        setDate(bookingData.end_date ? new Date(bookingData.end_date).toISOString().split("T")[0] : "");

        // Set initial cost centers
        if (bookingData.cost_center1) {
          const matched = constcenter1.find(c => c.value === bookingData.cost_center1?.value || c.id === bookingData.cost_center1?.id || c.value === String(bookingData.cost_center1));
          setSelectedCostCenter(matched || null);
        }
        if (bookingData.cost_center2) {
          const matched = constcenter2.find(c => c.value === bookingData.cost_center2?.value || c.id === bookingData.cost_center2?.id || c.value === String(bookingData.cost_center2));
          setSelectedCostCenterTwo(matched || null);
        }

        if (bookingData.set) {
          const matchedSet = setOptions.find(s => s.value === bookingData.set || s.label === bookingData.set);
          setSelectedSet(matchedSet || { value: bookingData.set, label: bookingData.set });
        }

        // Fetch companies for client dropdown
        try {
          const companiesResponse = await companyService.getAllCompanies(1, 1000);
          const companiesData = companiesResponse.data?.companies || [];
          const formattedCompanies = companiesData.map(company => ({
            value: company.id,
            label: company.name,
          }));
          setClientOptions(formattedCompanies);

          if (bookingData.client) {
            const matched = formattedCompanies.find(c =>
              c.value === bookingData.client?.id ||
              c.label === bookingData.client?.label ||
              c.label === bookingData.client
            );
            setSelectedClient(matched || null);
          }
        } catch (companyError) {
          console.error("Error fetching companies:", companyError);
        }

        // Set KPI value - handle both object and string/number formats
        const kpiValue = bookingData.kpi;
        if (typeof kpiValue === 'object' && kpiValue !== null) {
          // console.warn('KPI is an object, using empty string:', kpiValue);
          setKpi('');
        } else {
          setKpi(kpiValue || '');
        }

        // Fetch locations and set initial location
        const locations = await fetchLocations();
        
        // Set initial location value
        if (bookingData.location && locations.length > 0) {
          if (typeof bookingData.location === 'object' && bookingData.location !== null) {
            const locationOption = locations.find(loc => 
              loc.value === bookingData.location.id || 
              loc.label === bookingData.location.name || 
              loc.label === bookingData.location.short
            );
            setSelectedLocation(locationOption || null);
          } else if (typeof bookingData.location === 'string') {
            const locationOption = locations.find(loc => 
              loc.label === bookingData.location || 
              loc.value === bookingData.location
            );
            setSelectedLocation(locationOption || null);
          }
        }

        // Fetch users who can be assigned as proposedBy
        try {
          // console.log("Fetching users for proposedBy dropdown...");
          const usersResponse = await userService.getAllUsers();
          // console.log("Users API response:", usersResponse);
          
          if (usersResponse && usersResponse.data) {
            let users = [];
            
            // Handle different response structures
            if (Array.isArray(usersResponse.data)) {
              users = usersResponse.data;
            } else if (usersResponse.data.users && Array.isArray(usersResponse.data.users)) {
              users = usersResponse.data.users;
            } else {
              // console.log("Unexpected user data structure:", usersResponse.data);
            }
            
            // console.log("Found users:", users);
            
            if (users.length > 0) {
              // Filter for superadmin users only
              const superAdminUsers = users.filter(user => {
                const role = user.role || user.userRole || user.user_role;
                // console.log(`User ${user.name || user.email} has role:`, role);
                return role === 'superadmin' || role === 'admin' || role === 'planning';
              });
              
              // console.log("Filtered superadmin users:", superAdminUsers);
              
              // Format users for dropdown - use email as value instead of ID
              const formattedUsers = superAdminUsers.map(user => ({
                value: user.email || user.userEmail || user.user_email || `user-${user.id}`,
                label: user.name || user.username || user.email || `User ${user.id}`
              }));
              
              // console.log("Formatted users for dropdown:", formattedUsers);
              setSuperAdminOptions(formattedUsers);
              
              // Set initial proposedBy value if it exists
              if (bookingData.proposedBy) {
                // console.log("Looking for proposedBy user with value:", bookingData.proposedBy);
                const selectedUser = formattedUsers.find(option => 
                  option.value === bookingData.proposedBy || 
                  option.label === bookingData.proposedBy ||
                  // Also check if proposedBy is an ID and find the corresponding user
                  (superAdminUsers.find(u => (u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)) && 
                   option.value === (superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.email ||
                                    superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.userEmail ||
                                    superAdminUsers.find(u => u.id == bookingData.proposedBy || u.user_id == bookingData.proposedBy)?.user_email))
                );
                // console.log("Found proposedBy user:", selectedUser);
                setpropsoedBy(selectedUser || null);
              }
            } else {
              // console.log("No users found in response");
            }
          } else {
            // console.log("No user data in response");
          }
        } catch (userError) {
          console.error("Error fetching users:", userError);
          console.error("Error details:", userError.response?.data || userError.message);
          // Continue even if user fetch fails
        }
      } catch (error) {
        console.error("Error fetching booking details:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId, bookingStatusOptions, setOptions]);

  // Helper function to display location
  const getLocationDisplay = (bookingDetails) => {
    if (!bookingDetails || !bookingDetails.location) return "N/A";
    
    if (typeof bookingDetails.location === 'object' && bookingDetails.location !== null) {
      return bookingDetails.location.short || bookingDetails.location.name || bookingDetails.location.label || "N/A";
    } else if (typeof bookingDetails.location === 'string') {
      return bookingDetails.location;
    }
    
    return "N/A";
  };

  // Helper function to display time
  const getTimeDisplay = (bookingDetails, timeType) => {
    if (!bookingDetails) return "N/A";
    
    // Check multiple possible field names for start/end time
    if (timeType === 'start') {
      return bookingDetails.start_time || bookingDetails.startTime || "N/A";
    } else if (timeType === 'end') {
      return bookingDetails.end_time || bookingDetails.endTime || "N/A";
    }
    
    return "N/A";
  };

  // Helper function to display KPI as integer
  const getKPIDisplay = (bookingDetails) => {
    if (!bookingDetails || !bookingDetails.kpi) return "N/A";
    
    const kpiValue = bookingDetails.kpi;
    
    // Handle object KPI (shouldn't happen but just in case)
    if (typeof kpiValue === 'object' && kpiValue !== null) {
      return "N/A";
    }
    
    // Convert to number and round to integer
    const numericValue = parseFloat(kpiValue);
    if (!isNaN(numericValue)) {
      return Math.round(numericValue).toString();
    }
    
    return "N/A";
  };

  const updateStatus = async () => {
    if (status) {
      try {
        await bookingService.updateBookingStatus(bookingId, status.value);
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating status:", error);
        alert("Failed to update status. Please try again.");
      }
    } else {
      alert("Please select a status before updating");
    }
  };

  const updatePropsedBy = async () => {
    if (propsoedBy) {
      try {
        // console.log("Updating proposedBy with email address:", propsoedBy.value);
        // console.log("Selected user object:", propsoedBy);
        
        const updateData = { 
          proposedBy: propsoedBy.value,  // Now contains email address instead of ID
          proposed_by: propsoedBy.value  // Send both formats in case API expects different field name
        };
        
        // console.log("Sending update data:", updateData);
        await bookingService.updateBooking(bookingId, updateData);
        
        // console.log("ProposedBy updated successfully");
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating proposed by:", error);
        console.error("Error details:", error.response?.data || error.message);
        alert("Failed to update proposed by. Please try again.");
      }
    } else {
      alert("Please select a user before updating");
    }
  };

  const updateKpi = async () => {
    try {
      // Convert KPI to integer if it's a valid number
      let kpiValue = kpi;
      if (kpiValue && !isNaN(parseFloat(kpiValue))) {
        kpiValue = Math.round(parseFloat(kpiValue)).toString();
      }
      
      await bookingService.updateBooking(bookingId, { kpi: kpiValue });
      // Reload page instead of showing success alert
      window.location.reload();
    } catch (error) {
      console.error("Error updating KPI:", error);
      alert("Failed to update KPI. Please try again.");
    }
  };

  const updateLocation = async () => {
    if (selectedLocation) {
      try {
        await bookingService.updateBooking(bookingId, { 
          location_id: selectedLocation.value // Send only the location ID
        });
        // Reload page instead of showing success alert
        window.location.reload();
      } catch (error) {
        console.error("Error updating location:", error);
        alert("Failed to update location. Please try again.");
      }
    } else {
      alert("Please select a location before updating");
    }
  };

  const updateClient = async () => {
    if (selectedClient) {
      try {
        await bookingService.updateBooking(bookingId, { client: selectedClient });
        window.location.reload();
      } catch (error) {
        console.error("Error updating client:", error);
        alert("Failed to update client. Please try again.");
      }
    } else {
      alert("Please select a client before updating");
    }
  };

  const updateDate = async () => {
    if (date) {
      try {
        await bookingService.updateBooking(bookingId, { end_date: date, start_date: date });
        window.location.reload();
      } catch (error) {
        console.error("Error updating date:", error);
        alert("Failed to update date. Please try again.");
      }
    } else {
      alert("Please enter a date before updating");
    }
  };

  const updateStartTime = async () => {
    try {
      await bookingService.updateBooking(bookingId, { start_time: startTime, startTime });
      window.location.reload();
    } catch (error) {
      console.error("Error updating start time:", error);
      alert("Failed to update start time. Please try again.");
    }
  };

  const updateEndTime = async () => {
    try {
      await bookingService.updateBooking(bookingId, { end_time: endTime, endTime });
      window.location.reload();
    } catch (error) {
      console.error("Error updating end time:", error);
      alert("Failed to update end time. Please try again.");
    }
  };

  const updateSet = async () => {
    if (selectedSet) {
      try {
        await bookingService.updateBooking(bookingId, { set: selectedSet.value });
        window.location.reload();
      } catch (error) {
        console.error("Error updating set:", error);
        alert("Failed to update set. Please try again.");
      }
    } else {
      alert("Please select a set before updating");
    }
  };


  const updateCostCenters = async () => {
    try {
      await bookingService.updateBooking(bookingId, {
        cost_center1: selectedCostCenter || null,
        cost_center2: selectedCostCenterTwo || null,
      });
      window.location.reload();
    } catch (error) {
      console.error("Error updating cost centers:", error);
      alert("Failed to update cost centers. Please try again.");
    }
  };

  const handleStatusChange = (selectedOption) => {
    setStatus(selectedOption);
  };

  const poposeClick = async () => {
    try {
      const response = await bookingService.getBookingById(bookingId);
      const booking = response.data;
      
      if (booking && !booking.isBeingProcessed) {
        // If the order is not being processed, update it to being processed
        await bookingService.updateBooking(bookingId, {
          isBeingProcessed: true,
          processingBy: localStorage.getItem("userEmail"),
        });
        navigate(
          `/admin/propose?bookingId=${bookingId}&group=${booking.group}`
        );
      } else {
        alert(
          "This order is being processed by another user."
        );
      }
    } catch (error) {
      console.error("Error checking booking status:", error);
      alert("Failed to process this booking. Please try again.");
    }
  };

  // Render the booking details if available
  return (
    <div className="content-wrapper page">
      {loading ? (
        <div className="content-wrapper">Loading Order details...</div>
      ) : notFound ? (
        <div className="content-wrapper">No such Order exists.</div>
      ) : bookingDetails ? (
        <div>
          <div className="content-wrapper">
            <h1 style={{ marginBottom: "50px" }}>Order Details</h1>
          </div>
          <div className="content-wrapper content-wrapper-talent-list">
            <div className="list-tasks-header">
              <div style={{ flexBasis: "200px" }}>Client</div>
              <div style={{ flexBasis: "100px" }}>Date</div>
              <div style={{ flexBasis: "80px" }}>Option</div>
              <div style={{ flexBasis: "100px" }}>Position</div>
              <div style={{ flexBasis: "60px" }}>Set</div>
              <div style={{ flexBasis: "100px" }}>Location</div>
              <div style={{ flexBasis: "60px" }}>KPI</div>
              <div style={{ flexBasis: "80px" }}>Status</div>
              <div style={{ flexBasis: "80px" }}>Start</div>
              <div style={{ flexBasis: "80px" }}>End</div>
            </div>
            <div className="list-tasks-item">
              <div style={{ flexBasis: "200px" }}>
                {bookingDetails.client?.label || bookingDetails.client?.name || "N/A"}
              </div>
              <div style={{ flexBasis: "100px" }}>
                {bookingDetails.end_date ? new Date(bookingDetails.end_date).toLocaleDateString() : "N/A"}
              </div>
              <div style={{ flexBasis: "80px" }}>{bookingDetails.option || "N/A"}</div>
              <div style={{ flexBasis: "100px" }}>
                {bookingDetails.position?.label || bookingDetails.position?.name || "N/A"}
              </div>
              <div style={{ flexBasis: "60px" }}>{bookingDetails.set || "N/A"}</div>
              <div style={{ flexBasis: "100px" }}>
                {getLocationDisplay(bookingDetails)}
              </div>
              <div style={{ flexBasis: "60px" }}>
                {getKPIDisplay(bookingDetails)}
              </div>
              <div style={{ flexBasis: "80px" }}>{bookingDetails.status || "N/A"}</div>
              <div style={{ flexBasis: "80px" }}>
                {getTimeDisplay(bookingDetails, 'start')}
              </div>
              <div style={{ flexBasis: "80px" }}>
                {getTimeDisplay(bookingDetails, 'end')}
              </div>
            </div>
          </div>
          <div className="content-wrapper" style={{ marginTop: "30px" }}>
            <button
              className="button"
              onClick={poposeClick}
            >
              propose other talent
            </button>
            {/* <button
              className="button"
              onClick={() => navigate(`../edit?bookingId=${bookingId}`)}
            >
              edit all details
            </button> */}
          </div>
          <div className="content-wrapper" style={{ marginTop: "20px" }}>
            <p style={{ color: "#e65c00", fontWeight: "bold", padding: "10px", border: "1px solid #e65c00", borderRadius: "4px", backgroundColor: "#fff8f5" }}>
              Please note these updates will not send a notification to the talent! You need to notify the talent manually. If this is a confirmed order, please make sure you update the LANO order as well.
            </p>
          </div>
          <div className="content-wrapper" style={{ display: "flex", flexDirection: "column", gap: "30px" }}>
            {/* First row: Status and Propose By */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}> 
                <p style={{ padding:'10px'}}>Status:</p>
                <MemoizedSelect
                  value={status}
                  onChange={handleStatusChange}
                  options={bookingStatusOptions}
                  isClearable
                  placeholder="Select Status"
                  styles={customStyles}
                />
                <div style={{ marginTop: "20px" }}> 
                  <button className="button" onClick={updateStatus}>
                    Update Status
                  </button>
                </div>
              </div>
              <div style={{ width: "45%" }}> 
                <p style={{ padding:'10px'}}>Propose By:</p>
                <Select
                  value={propsoedBy}
                  onChange={setpropsoedBy}
                  options={superAdminOptions}
                  isClearable
                  placeholder="Select Proposed user"
                  styles={customStyles}
                />
                <div style={{ marginTop: "20px" }}> 
                  <button className="button" onClick={updatePropsedBy}>
                    Update Propose By
                  </button>
                </div>
              </div>
            </div>
            
            {/* Second row: KPI and Location */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}>
                <p style={{ padding:'10px'}}>KPI:</p>
                <input
                  type="text"
                  value={kpi}
                  onChange={(e) => setKpi(e.target.value)}
                  placeholder="Enter KPI"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateKpi}>
                    Update KPI
                  </button>
                </div>
              </div>
              <div style={{ width: "45%" }}>
                <p style={{ padding:'10px'}}>Location:</p>
                <MemoizedSelect
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  options={locationOptions}
                  isClearable
                  placeholder="Select Location"
                  styles={customStyles}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateLocation}>
                    Update Location
                  </button>
                </div>
              </div>
            </div>

            {/* Third row: Start Time and End Time */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Start Time:</p>
                <input
                  type="text"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  placeholder="Start Time"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateStartTime}>
                    Update Start Time
                  </button>
                </div>
              </div>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>End Time:</p>
                <input
                  type="text"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="End Time"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateEndTime}>
                    Update End Time
                  </button>
                </div>
              </div>
            </div>

            {/* Fourth row: Set */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Set:</p>
                <MemoizedSelect
                  value={selectedSet}
                  onChange={setSelectedSet}
                  options={setOptions}
                  isClearable
                  placeholder="Select Set"
                  styles={customStyles}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateSet}>
                    Update Set
                  </button>
                </div>
              </div>
            </div>

            {/* Fifth row: Client and Date */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Client:</p>
                <MemoizedSelect
                  value={selectedClient}
                  onChange={setSelectedClient}
                  options={clientOptions}
                  isClearable
                  placeholder="Select Client"
                  styles={customStyles}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateClient}>
                    Update Client
                  </button>
                </div>
              </div>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Date:</p>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
                <div style={{ marginTop: "20px" }}>
                  <button className="button" onClick={updateDate}>
                    Update Date
                  </button>
                </div>
              </div>
            </div>

            {/* Sixth row: Cost Centers */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Cost Center 1:</p>
                <MemoizedSelect
                  value={selectedCostCenter}
                  onChange={setSelectedCostCenter}
                  options={constcenter1}
                  isClearable
                  placeholder="Select Cost Center 1"
                  styles={customStyles}
                />
              </div>
              <div style={{ width: "45%" }}>
                <p style={{ padding: "10px" }}>Cost Center 2:</p>
                <MemoizedSelect
                  value={selectedCostCenterTwo}
                  onChange={setSelectedCostCenterTwo}
                  options={constcenter2}
                  isClearable
                  placeholder="Select Cost Center 2"
                  styles={customStyles}
                />
              </div>
            </div>
            <div style={{ marginTop: "20px" }}>
              <button className="button" onClick={updateCostCenters}>
                Update Cost Centers
              </button>
            </div>
          </div>

        </div>
      ) : null}
    </div>
  );
}
export default BookingDetailsPage;
